import {
  ROSTER_PALETTE_KEYS,
  type RosterEvaluationTrait,
  type RosterIndicator,
  type RosterPaletteKey,
  type RosterSettings,
  type Scholarship,
  type ScholarshipPlayer,
} from '../../../../types/scholarships';
import type { CSSProperties } from 'react';
import type { RosterSport } from '../../../../constants/sport';

/**
 * Everything the roster views need to turn a board's settings into what a coach sees: palette keys
 * into colours, a player's `bats` into a name colour, and trait / indicator ids into the options
 * they point at. Pure, so the rules are testable without rendering a board.
 */

/** A board that predates the settings (deploy skew) reads as one with nothing configured. */
export const EMPTY_ROSTER_SETTINGS: RosterSettings = {
  evaluationTraits: [],
  indicators: [],
  handednessColors: { left: 'black', right: 'black', switch: 'black' },
  armAngles: [],
  pitchShapes: [],
  pitchingRoles: [],
  playerTypes: [],
  playerStatuses: [],
};

export const rosterSettingsOf = (scholarship: Pick<Scholarship, 'rosterSettings'>): RosterSettings =>
  scholarship.rosterSettings ?? EMPTY_ROSTER_SETTINGS;

export const isPaletteKey = (value: string): value is RosterPaletteKey =>
  (ROSTER_PALETTE_KEYS as readonly string[]).includes(value);

/**
 * The CSS custom property a palette key paints with. An unknown key (one a newer API added) falls
 * back to the body text colour rather than to no colour at all.
 */
export const paletteColorVar = (key: string | null | undefined): string =>
  key != null && isPaletteKey(key) ? `var(--color-palette-${key})` : 'var(--color-text-black)';

/**
 * How a palette key paints a MARK: an indicator dot or a picker swatch, never text. The fill is the
 * key's mark shade, which reads as the colour's name, edged in its text shade so the boundary holds
 * 3:1 on white. Names keep `paletteColorVar`, because a name has to clear 4.5:1 as text.
 */
export const paletteMarkStyle = (key: string | null | undefined): CSSProperties =>
  key != null && isPaletteKey(key)
    ? {
        backgroundColor: `var(--color-palette-${key}-mark)`,
        boxShadow: `inset 0 0 0 1px var(--color-palette-${key})`,
      }
    : { backgroundColor: 'var(--color-text-black)' };

/** "red" → "Red", for colour pickers and screen-reader text. */
export const paletteLabel = (key: string): string =>
  key.length === 0 ? key : key[0].toUpperCase() + key.slice(1);

export type Handedness = 'left' | 'right' | 'switch';

/**
 * Which side a player hits from. `S` is what this app stores for a switch hitter; `B` ("both") is
 * accepted too because imported records use it. Anything else, including blank, is unknown.
 */
export const handednessOf = (bats: string | null | undefined): Handedness | null => {
  switch (bats?.trim().toUpperCase()) {
    case 'L':
      return 'left';
    case 'R':
      return 'right';
    case 'S':
    case 'B':
      return 'switch';
    default:
      return null;
  }
};

/** The palette key a player's name is painted in, or null when their side is unknown. */
export const handednessColorKey = (
  bats: string | null | undefined,
  settings: RosterSettings,
): string | null => {
  const side = handednessOf(bats);
  return side == null ? null : settings.handednessColors[side];
};

/** The name colour as a CSS value, or undefined to leave the name in the ordinary text colour. */
export const handednessColor = (
  bats: string | null | undefined,
  settings: RosterSettings,
): string | undefined => {
  const key = handednessColorKey(bats, settings);
  return key == null ? undefined : paletteColorVar(key);
};

export const HANDEDNESS_LABELS: Record<Handedness, string> = {
  left: 'Left-handed hitter (LHH)',
  right: 'Right-handed hitter (RHH)',
  switch: 'Switch hitter',
};

/** A player's trait, or null when they have none or it is one the board no longer has. */
export const resolveTrait = (
  traitId: string | null | undefined,
  settings: RosterSettings,
): RosterEvaluationTrait | null =>
  traitId == null ? null : settings.evaluationTraits.find((t) => t.id === traitId) ?? null;

/**
 * Trait pills are tinted so a card reads at a glance ("two Power bats, no Speed"). The default
 * traits have fixed colours; a coach's own traits take the next colour by their place in the list.
 * Red, black and gray are left out: red and black are the default name colours, and gray reads as
 * disabled.
 */
const DEFAULT_TRAIT_COLORS: Record<string, RosterPaletteKey> = {
  speed: 'teal',
  power: 'orange',
  hit: 'green',
  'defense-1st': 'blue',
  'speed-power': 'purple',
  command: 'pink',
  stuff: 'yellow',
};
const TRAIT_COLOR_CYCLE: readonly RosterPaletteKey[] = [
  'blue',
  'teal',
  'green',
  'orange',
  'purple',
  'pink',
  'yellow',
];

/** The palette key a trait's pill is tinted with. */
export const traitColorKey = (traitId: string, settings: RosterSettings): RosterPaletteKey => {
  // Own properties only: a coach's trait id is free text, and "constructor" or "__proto__" would
  // otherwise find Object.prototype members rather than falling through to the cycle.
  if (Object.hasOwn(DEFAULT_TRAIT_COLORS, traitId)) return DEFAULT_TRAIT_COLORS[traitId];
  const index = settings.evaluationTraits.findIndex((t) => t.id === traitId);
  return TRAIT_COLOR_CYCLE[(index < 0 ? 0 : index) % TRAIT_COLOR_CYCLE.length];
};

/**
 * A player's indicators in the player's own order, with ids the board no longer has dropped: the
 * API deliberately does not validate them on write, because settings change after the fact.
 */
export const resolveIndicators = (
  indicatorIds: readonly string[] | null | undefined,
  settings: RosterSettings,
): RosterIndicator[] =>
  (indicatorIds ?? []).flatMap((id) => {
    const found = settings.indicators.find((i) => i.id === id);
    return found ? [found] : [];
  });

/** "Draft Risk, Portal Risk" for an aria-label, or '' when there are none. */
export const indicatorNames = (
  indicatorIds: readonly string[] | null | undefined,
  settings: RosterSettings,
): string =>
  resolveIndicators(indicatorIds, settings)
    .map((i) => i.name)
    .join(', ');

/**
 * Friendly names for the default pitch-shape codes. Rows store the short code (the tables show
 * "SK/SL"); pickers show "SK (Sinker)". A code a coach added themselves has no entry and is shown
 * as typed.
 */
const PITCH_SHAPE_NAMES: Record<string, string> = {
  FF: 'Four-Seam',
  TS: 'Two-Seam',
  SK: 'Sinker',
  ASR: 'Cut Fastball',
  SL: 'Slider',
  CB: 'Curveball',
  SB: 'Sweeper',
  KN: 'Knuckle Curve',
  CH: 'Changeup',
  SPL: 'Splitter',
  CT: 'Cutter',
  FS: 'Forkball',
  // Softball shapes. CB above stays the baseball curve; softball boards store CR.
  RB: 'Rise Ball',
  DB: 'Drop Ball',
  CR: 'Curveball',
  SC: 'Screwball',
};

export const pitchShapeLabel = (code: string): string => {
  const name = PITCH_SHAPE_NAMES[code.trim().toUpperCase()];
  return name ? `${code} (${name})` : code;
};

/** "SK/SL" — the table cell. Blank when the pitcher has none. */
export const formatPitchShapes = (shapes: readonly string[] | null | undefined): string =>
  (shapes ?? []).join('/');

/**
 * Whether a board tracks arm angle at all. Softball pitchers all throw from the same underhand
 * slot, so a softball board hides the field everywhere rather than offering an empty picker.
 */
export const sportHasArmAngle = (sport: RosterSport): boolean => sport !== 'softball';

export type PitcherHand = 'RHP' | 'LHP';

export const isPitcherPosition = (position: string | null | undefined): position is PitcherHand =>
  position === 'RHP' || position === 'LHP';

/** Pitching fields apply when either of the player's positions is on the mound. */
export const playsPitcher = (
  player: Pick<ScholarshipPlayer, 'position' | 'secondaryPosition'>,
): boolean => isPitcherPosition(player.position) || isPitcherPosition(player.secondaryPosition);

/**
 * A board list's options with the value(s) the row holds kept in them, first. A label the board
 * has since removed from its list is still what the row holds; dropping it from the options would
 * blank the control and clear the value on the next save. One helper for every select and
 * checkbox list that reads a board list, so they cannot disagree about it.
 */
export const withCurrentOption = (
  options: readonly string[],
  current: string | readonly string[] | null | undefined,
): string[] => {
  const held = current == null ? [] : typeof current === 'string' ? [current] : current;
  const missing = [...new Set(held)].filter((value) => value !== '' && !options.includes(value));
  return [...missing, ...options];
};
